import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { getKycPartnerSubmission, submitKycToPartner } from 'api/kyc';
import { PermissionsV2 } from 'constants/permissions_v2';
import ConfirmationDialog from 'modules/credit-transactions/components/ConfirmationDialog';
import {
  DEFAULT_KYC_PARTNER_CODE,
  KYC_PARTNER_ACCOUNT_FIELDS,
  KYC_PARTNER_AUDIT_PARENT_KEY,
  KYC_PARTNER_DOCUMENT_TABLE_HEADERS,
} from 'modules/kyc/constants/constants';
import {
  formatKycPartnerLabel,
  formatPartnerAccountStatus,
  formatRfiStatus,
  getPartnerAccountStatusBadgeColor,
  getRfiStatusBadgeColor,
} from 'modules/kyc/utils/utils';
import { buildKybDetailSections } from 'modules/merchantinfo/utils/kybDetails';
import { showError, showToast } from 'utils/notifications';
import { formatDateTime } from 'utils/utils';
import PermissionWrapper from 'components/hoc/PermissionWrapper';
import JsonViewer from 'components/JsonViewer/JsonViewer';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';
import ShowKeyAndValue from 'components/ShowKeyAndValue/ShowKeyAndValue';

const NOT_AVAILABLE = '-';
const REDACTED_VALUE = 'Redacted';

const HEADER_FIELDS = [
  { label: 'KYC ID', key: 'kyc_id' },
  { label: 'Entity ID', key: 'entity_id' },
  { label: 'Customer ID', key: 'customer_id' },
  { label: 'Client ID', key: 'client_id' },
  { label: 'Partner Account ID', key: 'partner_account_id' },
  { label: 'Partner Status', key: 'partner_status' },
];

const partnerAccountEmptyMessage = (partner) =>
  `No ${partner} draft account exists for this KYC yet, so there is nothing to submit.`;

const submitConfirmMessage = (partner) =>
  `This will submit the existing ${partner} draft account for compliance review. This action cannot be undone. Make sure every KYC detail below is correct before proceeding.`;

const submitBlockedReason = (data, partner) => {
  if (!data?.partner_account_id) return `No ${partner} draft account exists for this KYC yet.`;

  return `KYC status ${data?.kyc_status ?? NOT_AVAILABLE} is not eligible for submission.`;
};

const toDetailRows = (details, keepAudit) =>
  (details ?? [])
    .filter((detail) => (detail?.parent_key === KYC_PARTNER_AUDIT_PARENT_KEY) === keepAudit)
    .map((detail) => ({
      key: detail?.key,
      parent: detail?.parent_key,
      value: detail?.redacted ? REDACTED_VALUE : detail?.value,
    }));

const formatTimestamp = (value) => (value ? formatDateTime(value) : NOT_AVAILABLE);

export default function KycPartnerSubmissionDrawer({ isOpen, onClose, kycId, partnerCode, onSubmitted }) {
  const partnerLabel = formatKycPartnerLabel(partnerCode ?? DEFAULT_KYC_PARTNER_CODE);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const toast = useToast();

  const fetchSubmission = useCallback(async () => {
    if (!kycId) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await getKycPartnerSubmission(kycId);

      setData(response?.data?.data ?? null);
    } catch (fetchError) {
      setError(fetchError);
    }
    setIsLoading(false);
  }, [kycId]);

  const refreshSubmission = useCallback(async () => {
    if (!kycId) return;

    setIsRefreshing(true);
    try {
      const response = await getKycPartnerSubmission(kycId);

      setData(response?.data?.data ?? null);
    } catch (refreshError) {
      showError(
        toast,
        `Failed to refresh from ${partnerLabel}`,
        refreshError,
        'Could not refresh this KYC submission.'
      );
    }
    setIsRefreshing(false);
  }, [kycId, toast]);

  const detailSections = useMemo(() => buildKybDetailSections(toDetailRows(data?.details, false)), [data]);

  const partnerAccount = data?.partner_account;
  const partnerAccountFetchError = data?.partner_account_fetch_error;
  const isSubmitDisabled = isSubmitting || !data?.can_submit;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await submitKycToPartner({ kyc_id: kycId });
      const result = response?.data?.data;
      const accountId = result?.individual_account_id || NOT_AVAILABLE;

      showToast(
        toast,
        `Submitted to ${partnerLabel}`,
        `Account ${accountId} is now ${result?.partner_status}.`,
        'success'
      );
      setIsConfirmOpen(false);
      onSubmitted?.();
    } catch (submitError) {
      showError(
        toast,
        `Submission to ${partnerLabel} failed`,
        submitError,
        `Could not submit this KYC to ${partnerLabel}.`
      );
      setIsConfirmOpen(false);
    }
    await refreshSubmission();
    setIsSubmitting(false);
  };

  const renderSections = (sections) =>
    sections?.map((section) => (
      <Box key={section?.parent} mt={4}>
        <Heading as='h2' size='md' mb={3}>
          {section?.label}
        </Heading>
        <Flex direction='column' gap={3}>
          {section?.rows?.map((row) => (
            <ShowKeyAndValue
              key={row?.id}
              keyName={row?.keyName}
              value={row?.value}
              linkText={row?.linkText}
              helperText={row?.helperText}
            />
          ))}
        </Flex>
      </Box>
    ));

  const renderPartnerAccountDocuments = () => {
    if (!partnerAccount?.documents?.length) {
      return (
        <Text fontSize='sm' color='gray.500' mt={2}>
          {partnerLabel} has not asked for any document on this account yet.
        </Text>
      );
    }

    return (
      <TableContainer borderRadius='lg' border='1px solid' borderColor='gray.200' overflowX='auto' mt={3}>
        <Table variant='simple' size='sm'>
          <Thead bg='gray.100'>
            <Tr>
              {KYC_PARTNER_DOCUMENT_TABLE_HEADERS.map((heading) => (
                <Th key={heading} fontSize='xs' fontWeight='bold' color='gray.700'>
                  {heading}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {partnerAccount.documents.map((doc) => (
              <Tr key={`${doc?.type}_${doc?.status}`} fontSize='sm'>
                <Td fontWeight='medium'>{doc?.type}</Td>
                <Td color='gray.600'>{doc?.status || NOT_AVAILABLE}</Td>
                <Td color='gray.600'>{doc?.required ? 'Yes' : 'No'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  const renderPartnerAccount = () => {
    if (partnerAccountFetchError) {
      return (
        <Alert status='warning' alignItems='flex-start' borderRadius='md'>
          <AlertIcon />
          <Box>
            <AlertTitle>Could not read the live record from {partnerLabel}</AlertTitle>
            <AlertDescription>
              <Box
                as='pre'
                fontSize='xs'
                mt={2}
                p={3}
                bg='gray.50'
                borderRadius='md'
                maxH='200px'
                overflowY='auto'
                whiteSpace='pre-wrap'
                wordBreak='break-all'
              >
                {partnerAccountFetchError}
              </Box>
              <Button size='sm' variant='outline' mt={3} onClick={refreshSubmission} isLoading={isRefreshing}>
                Retry
              </Button>
            </AlertDescription>
          </Box>
        </Alert>
      );
    }

    if (!partnerAccount) {
      return (
        <Alert status='info' borderRadius='md'>
          <AlertIcon />
          {partnerAccountEmptyMessage(partnerLabel)}
        </Alert>
      );
    }

    return (
      <>
        <Flex direction='column' gap={3}>
          {KYC_PARTNER_ACCOUNT_FIELDS.map((field) => (
            <ShowKeyAndValue
              key={field?.key}
              keyName={field?.label}
              value={partnerAccount?.[field?.key] || NOT_AVAILABLE}
            />
          ))}
        </Flex>

        {Boolean(partnerAccount?.errors?.length) && (
          <Alert status='error' alignItems='flex-start' borderRadius='md' mt={4}>
            <AlertIcon />
            <Box>
              <AlertTitle>
                {partnerLabel} reported {partnerAccount.errors.length} issue(s) on this account
              </AlertTitle>
              <AlertDescription>
                <VStack align='left' gap={1} mt={2}>
                  {partnerAccount.errors.map((issue, index) => (
                    <Text key={`${issue?.type}_${index}`} fontSize='sm'>
                      <Text as='span' fontWeight='bold'>
                        {issue?.type || 'issue'}
                      </Text>
                      {` — ${issue?.description}`}
                      {issue?.target?.data?.property ? ` (${issue.target.data.property})` : ''}
                      {issue?.target?.scope ? ` [scope: ${issue.target.scope}]` : ''}
                    </Text>
                  ))}
                </VStack>
              </AlertDescription>
            </Box>
          </Alert>
        )}

        <Heading as='h2' size='md' mt={4} mb={1}>
          Documents on {partnerLabel}
        </Heading>
        {renderPartnerAccountDocuments()}

        <Accordion allowToggle mt={4}>
          <AccordionItem>
            <AccordionButton>
              <Box flex='1' textAlign='left' fontWeight='medium'>
                Raw {partnerLabel} record
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              <JsonViewer jsonContent={partnerAccount} height='360px' />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </>
    );
  };

  useEffect(() => {
    if (isOpen) {
      fetchSubmission();
    }
  }, [isOpen, fetchSubmission]);

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='xl'>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth='1px'>{partnerLabel} KYC submission</DrawerHeader>

          <DrawerBody>
            <LoadingErrorWrapper
              isLoading={isLoading}
              error={error}
              errorTitle={`Error fetching ${partnerLabel} submission details.`}
            >
              {data && (
                <VStack align='left' gap={4} pb={4}>
                  <Box>
                    <HStack spacing={3} mt={4} align='center'>
                      <Badge colorScheme={getRfiStatusBadgeColor(data?.kyc_status)} rounded='full' px={3}>
                        {formatRfiStatus(data?.kyc_status)}
                      </Badge>
                      {data?.partner_status && (
                        <Badge
                          colorScheme={getPartnerAccountStatusBadgeColor(data?.partner_status)}
                          rounded='full'
                          px={3}
                        >
                          {formatPartnerAccountStatus(data?.partner_status)}
                        </Badge>
                      )}
                    </HStack>
                    <Flex direction='column' gap={3} mt={3}>
                      {HEADER_FIELDS.map((field) => (
                        <ShowKeyAndValue
                          key={field?.key}
                          keyName={field?.label}
                          value={data?.[field?.key] || NOT_AVAILABLE}
                        />
                      ))}
                      <ShowKeyAndValue
                        keyName='Pending Documents'
                        value={data?.pending_documents?.join(', ') || NOT_AVAILABLE}
                      />
                    </Flex>
                  </Box>

                  <Divider variant='dashed' />

                  <Box>
                    <Heading as='h1' size='lg' mb={3}>
                      KYC details
                    </Heading>
                    {detailSections?.length ? (
                      renderSections(detailSections)
                    ) : (
                      <Text color='gray.500'>No KYC details recorded for this KYC.</Text>
                    )}
                  </Box>

                  {Boolean(data?.last_submit_error) && (
                    <>
                      <Divider variant='dashed' />

                      <Alert status='error' alignItems='flex-start' borderRadius='md'>
                        <AlertIcon />
                        <Box>
                          <AlertTitle>Last submission to {partnerLabel} failed</AlertTitle>
                          <AlertDescription>
                            <Box
                              as='pre'
                              fontSize='xs'
                              mt={2}
                              p={3}
                              bg='gray.50'
                              borderRadius='md'
                              maxH='200px'
                              overflowY='auto'
                              whiteSpace='pre-wrap'
                              wordBreak='break-all'
                            >
                              {data.last_submit_error}
                            </Box>
                          </AlertDescription>
                        </Box>
                      </Alert>
                    </>
                  )}

                  <Divider variant='dashed' />

                  <Box>
                    <Flex justify='space-between' align='center' mb={3}>
                      <Heading as='h1' size='lg'>
                        {partnerLabel} — Live Partner Record
                      </Heading>
                      <Button size='sm' variant='outline' onClick={refreshSubmission} isLoading={isRefreshing}>
                        Refresh from {partnerLabel}
                      </Button>
                    </Flex>
                    {Boolean(data?.partner_account_fetched_at) && (
                      <Text fontSize='xs' color='gray.500' mb={3}>
                        Fetched {formatTimestamp(data?.partner_account_fetched_at)}
                      </Text>
                    )}
                    {renderPartnerAccount()}
                  </Box>

                  {!data?.can_submit && (
                    <Text fontSize='sm' color='gray.600'>
                      {submitBlockedReason(data, partnerLabel)}
                    </Text>
                  )}
                </VStack>
              )}
            </LoadingErrorWrapper>
          </DrawerBody>

          <DrawerFooter borderTopWidth='1px'>
            <Button variant='outline' mr={3} onClick={onClose}>
              Close
            </Button>
            <PermissionWrapper permission={PermissionsV2.MANAGE_KYB_COMPLIANCE_OPERATIONS}>
              <Button
                colorScheme='blue'
                onClick={() => setIsConfirmOpen(true)}
                isLoading={isSubmitting}
                isDisabled={isSubmitDisabled}
              >
                Submit to {partnerLabel}
              </Button>
            </PermissionWrapper>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleSubmit}
        title={`Submit KYC to ${partnerLabel}`}
        message={submitConfirmMessage(partnerLabel)}
        confirmLabel='Confirm & Submit'
        isLoading={isSubmitting}
      />
    </>
  );
}
